import { useEffect, useState } from 'react';

/**
 * Reading things out loud.
 *
 * The browser's own speech synthesis, which means a real spoken voice
 * with nothing to download and no audio to licence. It is not a hired
 * voice artist and does not pretend to be - but it reads at a person's
 * pace, pauses where the writing pauses, and can be turned off.
 *
 * Lives here rather than in one lab because two different things now
 * talk: the boot loader journey narrates itself, and the tutor explains
 * whatever section a learner is looking at.
 */

export const canSpeak = () => typeof window !== 'undefined' && Boolean(window.speechSynthesis);

/**
 * The best voice this device has.
 *
 * Voices load asynchronously nearly everywhere, so the list is read
 * again on voiceschanged rather than once at start-up - ask too early
 * and you get an empty array and the robot default.
 */
export function useVoice() {
  const [voice, setVoice] = useState(null);

  useEffect(() => {
    if (!canSpeak()) return undefined;
    const synth = window.speechSynthesis;

    const pick = () => {
      const english = synth.getVoices().filter((v) => v.lang.startsWith('en'));
      if (!english.length) return;
      /*
       * Indian English first, by a wide margin.
       *
       * This is a platform for Indian schools - the prices are in
       * rupees and the children in it are called Aarav and Meera - so a
       * narrator with an American accent is the odd one out, not the
       * default. Windows ships Heera and Ravi; Android has its own;
       * macOS has Rishi.
       *
       * The rest of the scoring only settles ties: whatever the
       * platform calls its good voices, then a local voice over a
       * network one, because a network voice lags before the first word
       * and sounds like the page has hung.
       *
       * If a device has no Indian voice at all it still speaks, in
       * whatever English it does have. A narrator in the wrong accent
       * beats no narrator.
       */
      const score = (v) => (
        (/^en-IN/i.test(v.lang) ? 16 : 0)
        + (/natural|neural|enhanced|premium/i.test(v.name) ? 4 : 0)
        + (v.localService ? 2 : 0)
        + (/en-GB/i.test(v.lang) ? 1 : 0)
      );
      setVoice([...english].sort((a, b) => score(b) - score(a))[0]);
    };

    pick();
    synth.addEventListener('voiceschanged', pick);
    return () => synth.removeEventListener('voiceschanged', pick);
  }, []);

  return voice;
}

/**
 * Say one thing, stopping whatever was being said before.
 *
 * Rate sits slightly under normal and pitch is left alone: faster
 * sounds hurried, higher sounds synthetic, and this has to hold a
 * child's attention for minutes at a time.
 */
export function speak(text, voice, { onStart, onEnd } = {}) {
  if (!canSpeak() || !text) return false;
  const synth = window.speechSynthesis;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(text);
  if (voice) u.voice = voice;
  // Asked for even when a voice is set: some engines use the language
  // to choose pronunciation rules, and an engine with no en-IN voice at
  // all will still lean the right way if it can.
  u.lang = voice?.lang ?? 'en-IN';
  u.rate = 0.94;
  u.pitch = 1;
  u.volume = 1;
  u.onstart = onStart;
  u.onend = onEnd;
  u.onerror = onEnd;
  synth.speak(u);
  return true;
}

export function hush() {
  if (canSpeak()) window.speechSynthesis.cancel();
}
